import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ToggleLeft, ToggleRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface UserStatusProps {
  userRole: "talent" | "recruiter" | null;
  isOpenToJobs: boolean;
  isHiring: boolean;
  setIsOpenToJobs: (value: boolean) => void;
  setIsHiring: (value: boolean) => void;
}

export const UserStatus = ({ 
  userRole, 
  isOpenToJobs, 
  isHiring, 
  setIsOpenToJobs, 
  setIsHiring 
}: UserStatusProps) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const handleStatusToggle = async (status: 'jobs' | 'hiring') => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.log("No valid session found for status toggle");
      return;
    }

    try {
      if (status === 'jobs') {
        const { error } = await supabase
          .from('profiles')
          .update({ is_open_to_jobs: !isOpenToJobs })
          .eq('id', session.user.id);

        if (error) throw error;
        setIsOpenToJobs(!isOpenToJobs);
        toast({
          title: !isOpenToJobs ? "You're now open to jobs" : "You're no longer open to jobs",
          description: "Your status has been updated",
        });
      } else {
        const { error } = await supabase
          .from('profiles')
          .update({ is_hiring: !isHiring })
          .eq('id', session.user.id);

        if (error) throw error;
        setIsHiring(!isHiring);
        toast({
          title: !isHiring ? "You're now hiring" : "You're no longer hiring",
          description: "Your status has been updated",
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update your status",
        variant: "destructive",
      });
    }
  };

  const getToggleText = () => {
    const currentLang = i18n.language;
    if (userRole === "talent") {
      return currentLang === "pt" ? "Disponível para Trabalho" : "Open to jobs";
    } else {
      return currentLang === "pt" ? "A Recrutar" : "Hiring";
    }
  };

  return (
    <div className="flex items-center gap-4 flex-shrink-0">
      {userRole === "talent" && (
        <Button
          variant="ghost"
          className={`flex items-center gap-2 whitespace-nowrap ${isOpenToJobs ? 'text-green-500' : 'text-gray-500'}`}
          onClick={() => handleStatusToggle('jobs')}
        >
          {isOpenToJobs ? (
            <ToggleRight className="h-5 w-5" />
          ) : (
            <ToggleLeft className="h-5 w-5" />
          )}
          <span className="hidden sm:inline">{getToggleText()}</span>
        </Button>
      )}
      {userRole === "recruiter" && (
        <Button
          variant="ghost"
          className={`flex items-center gap-2 whitespace-nowrap ${isHiring ? 'text-green-500' : 'text-gray-500'}`}
          onClick={() => handleStatusToggle('hiring')}
        >
          {isHiring ? (
            <ToggleRight className="h-5 w-5" />
          ) : (
            <ToggleLeft className="h-5 w-5" />
          )}
          <span className="hidden sm:inline">{getToggleText()}</span>
        </Button>
      )}
    </div>
  );
};